import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './PromoCodeGenerator.css';

type GenerateMode = 'single' | 'bulk';

interface GeneratorForm {
  couponTemplate: string;
  campaign: string;
  channel: string;
  prefix: string;
  maxRedemptions: string;
  expiresAt: string;
  targetAudience: string;
  notes: string;
  count: number;
}

const TEMPLATE_OPTIONS = [
  { value: '', label: '-- Select Template --' },
  { value: 'launch_special', label: 'Launch Special' },
  { value: 'partner_referral', label: 'Partner Referral' },
  { value: 'first_month_free', label: 'First Month Free' },
  { value: 'social_campaign', label: 'Social Campaign' },
  { value: 'win_back', label: 'Win Back' },
  { value: 'annual_incentive', label: 'Annual Incentive' },
  { value: 'retention_save', label: 'Retention Save' },
  { value: 'conference_booth', label: 'Conference Booth' },
];

const CHANNEL_OPTIONS = [
  { value: '', label: '-- Select Channel --' },
  { value: 'email', label: 'Email' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'partner', label: 'Partner' },
  { value: 'event', label: 'Event' },
  { value: 'direct_mail', label: 'Direct Mail' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
];

const INITIAL_FORM: GeneratorForm = {
  couponTemplate: '',
  campaign: '',
  channel: '',
  prefix: 'BSO',
  maxRedemptions: '',
  expiresAt: '',
  targetAudience: '',
  notes: '',
  count: 10,
};

const PromoCodeGenerator: React.FC = () => {
  const { getAccessToken } = useAuth();

  const [generateMode, setGenerateMode] = useState<GenerateMode>('single');
  const [form, setForm] = useState<GeneratorForm>({ ...INITIAL_FORM });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedCodes, setGeneratedCodes] = useState<string[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  const validate = (): string | null => {
    if (!form.couponTemplate) return 'Please select a coupon template.';
    if (!form.campaign.trim()) return 'Campaign name is required.';
    if (!form.channel) return 'Please select a distribution channel.';
    if (generateMode === 'bulk' && (form.count < 1 || form.count > 500)) {
      return 'Bulk count must be between 1 and 500.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedCodes([]);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        return;
      }

      const endpoint =
        generateMode === 'single'
          ? API_ENDPOINTS.promoCodeGenerate()
          : API_ENDPOINTS.promoCodeGenerateBulk();

      const payload: any = {
        couponTemplate: form.couponTemplate,
        campaign: form.campaign.trim(),
        channel: form.channel,
        prefix: form.prefix.trim() || 'BSO',
      };

      if (form.maxRedemptions) {
        payload.maxRedemptions = parseInt(form.maxRedemptions, 10);
      }
      if (form.expiresAt) {
        payload.expiresAt = form.expiresAt;
      }
      if (form.targetAudience.trim()) {
        payload.targetAudience = form.targetAudience.trim();
      }
      if (form.notes.trim()) {
        payload.notes = form.notes.trim();
      }
      if (generateMode === 'bulk') {
        payload.count = form.count;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Generation failed (${response.status})`);
      }

      const data = await response.json();

      if (generateMode === 'single' && data.promoCode) {
        setGeneratedCodes([data.promoCode]);
      } else if (data.promoCodes && Array.isArray(data.promoCodes)) {
        setGeneratedCodes(data.promoCodes);
      } else if (data.code) {
        setGeneratedCodes([data.code]);
      } else {
        setGeneratedCodes(['Code generated successfully']);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate promo code(s).');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="promo-generator">
      <div className="mode-toggle">
        <button
          className={`mode-btn ${generateMode === 'single' ? 'active' : ''}`}
          onClick={() => setGenerateMode('single')}
          type="button"
        >
          Single Code
        </button>
        <button
          className={`mode-btn ${generateMode === 'bulk' ? 'active' : ''}`}
          onClick={() => setGenerateMode('bulk')}
          type="button"
        >
          Bulk Generate
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>x</button>
        </div>
      )}

      {generatedCodes.length > 0 && (
        <div className="success-result">
          <div className="success-header">
            {generatedCodes.length === 1
              ? 'Promo code generated successfully'
              : `${generatedCodes.length} promo codes generated successfully`}
          </div>
          <div className="codes-list">
            {generatedCodes.map((code, idx) => (
              <div key={idx} className="code-display">
                <span className="code-text">{code}</span>
                <button
                  className="copy-btn"
                  onClick={() => handleCopy(code)}
                  type="button"
                >
                  {copied === code ? 'Copied' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <form className="generator-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="couponTemplate">Coupon Template *</label>
            <select
              id="couponTemplate"
              value={form.couponTemplate}
              onChange={(e) => setForm({ ...form, couponTemplate: e.target.value })}
            >
              {TEMPLATE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="campaign">Campaign *</label>
            <input
              id="campaign"
              type="text"
              placeholder="e.g., Q1 Launch, Summer 2026"
              value={form.campaign}
              onChange={(e) => setForm({ ...form, campaign: e.target.value })}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="channel">Channel *</label>
            <select
              id="channel"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
            >
              {CHANNEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="prefix">Code Prefix</label>
            <input
              id="prefix"
              type="text"
              placeholder="BSO"
              value={form.prefix}
              onChange={(e) => setForm({ ...form, prefix: e.target.value })}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="maxRedemptions">Max Redemptions</label>
            <input
              id="maxRedemptions"
              type="number"
              min="1"
              placeholder="Unlimited if blank"
              value={form.maxRedemptions}
              onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="expiresAt">Expiration Date</label>
            <input
              id="expiresAt"
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="targetAudience">Target Audience</label>
            <input
              id="targetAudience"
              type="text"
              placeholder="e.g., New customers, Enterprise tier"
              value={form.targetAudience}
              onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
            />
          </div>
          {generateMode === 'bulk' && (
            <div className="form-group">
              <label htmlFor="count">Number of Codes *</label>
              <input
                id="count"
                type="number"
                min="1"
                max="500"
                value={form.count}
                onChange={(e) =>
                  setForm({ ...form, count: Math.min(500, Math.max(1, parseInt(e.target.value, 10) || 1)) })
                }
              />
            </div>
          )}
        </div>

        <div className="form-row full-width">
          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              rows={3}
              placeholder="Internal notes about this promo code..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>

        <button className="generate-btn" type="submit" disabled={loading}>
          {loading
            ? 'Generating...'
            : generateMode === 'single'
            ? 'Generate Code'
            : `Generate ${form.count} Codes`}
        </button>
      </form>
    </div>
  );
};

export default PromoCodeGenerator;
